import {
  ALWAYS_VISIBLE_ITEM_KEYS,
  canonicalSectionTitleFor,
  getNavSections,
  matchedBaseFor,
  MASTER_EXCLUDED_KEYS,
  NAV_ITEMS,
  NAV_RESOURCE,
  ROLE_PANEL_ONLY_ITEM_KEYS,
} from 'constants/navigation';
import {
  isAdminRole,
  isCollaboratorRole,
  isJuryMemberRole,
  isMasterRole,
  isMentorRole,
  isStartupFounderRole,
} from 'util/access';

/**
 * Which modules this user can actually see — the ONE answer to that question.
 *
 * It used to be answered in three places that could disagree:
 *
 *   1. Sidebar        — the full rule, including modules a role was granted
 *                       but whose own panel never declared them.
 *   2. DashboardLayout — resolved the page title and breadcrumb from the role's
 *                       STATIC layout, so a granted module produced neither.
 *   3. resolveLandingPath — walked the static layout too, so a user whose only
 *                       modules were granted ones was sent to "No modules"
 *                       while the sidebar happily listed them.
 *
 * (3) was not theoretical: a mentor holding only `mentors:read` has Stakeholders
 * in their sidebar and got `null` from the landing resolver.
 *
 * Everything here is about LABELS AND LINKS. It is not an authorisation check —
 * the route guards and the API decide what may actually open. Getting this wrong
 * shows or hides a menu entry; it never widens access.
 */

const EVALUATION_MODULES = new Set(['evaluation-processes', 'evaluation-matrix']);

/**
 * @param {object}   ctx
 * @param {string}   ctx.role
 * @param {object}   ctx.principal          — for the admin-capability check
 * @param {boolean}  ctx.hasCenter
 * @param {array}    ctx.assignments        — extra panels held (mentor/jury/founder)
 * @param {function} ctx.can                — (resource, action) => boolean
 * @param {function} ctx.hasResourceGrant   — record-scoped collaboration grants
 * @param {boolean}  ctx.permsLoaded        — false ⇒ fail OPEN (see below)
 * @param {string[]} ctx.roleNames          — every role held (multi-role)
 * @returns {{ sections: Array, items: Array }}
 */
export function visibleNav({
  role,
  principal,
  hasCenter = false,
  assignments = [],
  can,
  hasResourceGrant = () => false,
  permsLoaded = true,
  roleNames = [],
}) {
  if (typeof can !== 'function') return { sections: [], items: [] };

  const isAdmin = isAdminRole(principal);

  // Show a module only if the role has `read` on its resource. Fail OPEN:
  // ungated items (no resource mapping) and sessions where the matrix hasn't
  // loaded (demo/local/offline) show everything, so nobody is left with an
  // empty menu. The backend enforces permissions on every API call regardless.
  const canSeeItem = (item) => {
    // Permission decides, not the admin flag. `isAdmin ||` here meant a centre
    // admin kept Programs and the two Evaluation modules however their matrix
    // was set — withheld everywhere else and still listed here, which reads as
    // a bug rather than a restriction.
    if (item.key === 'programs') {
      return can('applications', 'read') || hasResourceGrant('applications');
    }
    // A Collaborator's access to an Evaluation Process/Matrix is scoped to the
    // one Program they were invited to (opened from that Program, not this
    // module list) — never the reusable-template catalogue these two links
    // manage, so they're excluded here the same way Workspace/My Reviews are.
    if (EVALUATION_MODULES.has(item.key)) return !isCollaboratorRole(role) && can('evaluations', 'read');
    // "My Reviews" is the assigned reviewer's queue; admins review through the
    // Programs module instead. A Collaborator is a record-scoped guest with no
    // reviewer assignment, so it would only ever render empty for them.
    if (item.key === 'my-reviews') return !isAdmin && !isCollaboratorRole(role);
    // Workspace and its My Invitations sub-item are the to-do module's own
    // assignment inbox; a Collaborator is never a to-do assignee.
    if (item.key === 'todos' || item.key === 'todo-invitations') {
      return !isCollaboratorRole(role) && (can('todos', 'read') || !permsLoaded);
    }
    // Collaborations is mapped onto `todos` below (it has always lived inside
    // the Workspace section, gated the same way) — but it is now ALSO its own
    // real, separately-grantable permission (`collaboration:read`), which a
    // pure Collaborator account holds without ever holding `todos:read` (see
    // `todos` case above, which explicitly excludes them). Checking either
    // permission keeps every existing role's Workspace-based visibility
    // exactly as it was, while letting a Collaborator see it via their own
    // grant instead of via Workspace access they don't have.
    if (item.key === 'collaborations') {
      return !permsLoaded || can('collaboration', 'read') || can('todos', 'read') || hasResourceGrant('todos');
    }
    // A few pages are the role's own workspace rather than a granted module — a
    // founder's Startup Profile. Withholding the related verb makes them
    // READ-ONLY inside (each page checks `can(x,'write')`); removing them would
    // leave the panel broken rather than restricted. A mentor's Calendar is NOT
    // one of them: it is the Sessions module and follows `mentoring_sessions`.
    if (ALWAYS_VISIBLE_ITEM_KEYS.has(item.key)) return true;
    const resource = NAV_RESOURCE[item.key];
    if (!resource || !permsLoaded) return true;
    return can(resource, 'read') || hasResourceGrant(resource);
  };

  const roleSections = getNavSections(role, hasCenter, assignments)
    .map((section) => ({ ...section, items: section.items.filter(canSeeItem) }))
    .filter((section) => section.items.length > 0);

  // A module the role's own panel doesn't carry, but the role has been GRANTED,
  // still has to appear — otherwise ticking "Fund Catalog" for a founder shows
  // them nothing. It is placed in the section it normally belongs to, NOT in a
  // separate "granted" group: the sidebar lists the modules this role can open,
  // and where a grant came from is not something the reader needs to see.
  //
  // Deduped by PATH as well as key: a role panel may carry the same screen under
  // its own key (a mentor's 'mentor-calendar' is '/dashboard/calendar').
  const shownKeys = new Set(roleSections.flatMap((s) => s.items.map((i) => i.key)));
  const shownPaths = new Set(roleSections.flatMap((s) => s.items.map((i) => i.path)));
  // Resources the role's OWN panel already has a screen for. A founder's "Open
  // Programs" and the centre's "Programs" are both `applications`; a mentor's
  // "Scheduled Sessions" and the centre's "Mentoring" are both
  // `mentoring_sessions`. The role holds those verbs to work its own side of the
  // relationship, so adding the centre's version of the same module on top is
  // the same mistake in the other direction.
  const ownResources = new Set(
    // An item may also `covers` resources it answers for inside the page — a
    // founder's Startup Profile carries their own documents, so holding
    // `documents:*` never adds the centre's Document Verification.
    roleSections
      .flatMap((s) => s.items.flatMap((i) => [NAV_RESOURCE[i.key], ...(i.covers || [])]))
      .filter(Boolean),
  );

  const holdsStaffRole = (roleNames || []).some(
    (name) => name
      && !isStartupFounderRole(name)
      && !isMentorRole(name)
      && !isJuryMemberRole(name)
      && !isCollaboratorRole(name),
  );

  const grantedExtras = NAV_ITEMS.filter((item) => {
    if (shownKeys.has(item.key) || shownPaths.has(item.path)) return false;
    // Already covered by a screen this panel carries — a genuinely NEW grant
    // (Fund Catalog for a founder) has a resource nothing here answers for.
    // Skipped when the user ALSO holds a staff role: access is the union of
    // every role held, so a founder who is also a Program Manager gets the
    // centre's Programs too, not only their founder "Open Programs".
    if (!holdsStaffRole && ownResources.has(NAV_RESOURCE[item.key])) return false;
    // A role panel's own screens are never granted into someone else's sidebar.
    // A founder's "My Funding" and a centre's Funding share a verb, so without
    // this a Center Admin holding it for their OWN screen was handed the
    // founder's personal inbox too.
    if (ROLE_PANEL_ONLY_ITEM_KEYS.has(item.key)) return false;
    // The master's `can()` is always true, so without this every module taken
    // out of its panel (Centers, Super Admins) would come straight back here.
    if (isMasterRole(role) && MASTER_EXCLUDED_KEYS.has(item.key)) return false;
    const resource = NAV_RESOURCE[item.key];
    if (!resource || !permsLoaded) return false;
    return can(resource, 'read');
  });

  const sections = roleSections.map((section) => ({
    ...section,
    items: [...section.items],
  }));
  grantedExtras.forEach((item) => {
    const title = canonicalSectionTitleFor(item.key);
    const target = sections.find((section) => section.title === title);
    if (target) target.items.push(item);
    else sections.push({ title, items: [item] });
  });

  return { sections, items: sections.flatMap((s) => s.items) };
}

/**
 * The item + section a URL belongs to, resolved against what this user SEES.
 *
 * Same matcher and same tie-break as the global resolvers (longest matched base
 * wins, ties keep source order so the role's own item beats a granted one), but
 * scoped to the visible list — so the header title, the breadcrumb and the
 * sidebar highlight are answering from one source instead of three.
 *
 * Returns nulls when nothing matches; callers fall back to the global resolvers,
 * which cover pages that have no nav item of their own (Profile, Settings).
 */
export function resolveInSections(sections, pathname) {
  let best = null;
  sections.forEach((section) => {
    section.items.forEach((item) => {
      const base = matchedBaseFor(item, pathname);
      if (base && (!best || base.length > best.base.length)) {
        best = { item, base, section: section.title };
      }
    });
  });
  return { item: best?.item || null, section: best?.section || null };
}

export default visibleNav;
